"use client";
import type { ConversationData } from "@/features/chat/Types/conversations";

import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Flag,
  Loader2,
  MessageSquare,
  Minimize2,
  MoreHorizontal,
  Paperclip,
  Send,
  Users,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useNavigate } from "react-router-dom";

import { useChat } from "../hooks/useChat";
import { ConversationView } from "../components/ConversationView";
import { ReportDialog } from "../components/ReportDialog";
import { EmojiPickerButton } from "../components/EmojiPickerButton";
import { FilePreview } from "../components/FilePreview";
import { TypingIndicator } from "../components/TypingIndicator";
import { RateLimitIndicator } from "../components/RateLimitIndicator";
import { StorageUsageIndicator } from "../components/StorageUsageIndicator";
import { WorkspaceBlockingIndicator } from "../components/WorkspaceBlockingIndicator";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/NonPortalDropdownMenu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import { STUDENT_WORKSPACE_PATHS, TUTOR_WORKSPACE_PATHS } from "@/routes/paths";
import { DragDropOverlay } from "@/features/chat/components/DragDropOverlay.tsx";

interface PopupChatProps {
  conversation: ConversationData;
  isMinimized: boolean;
  onClose: () => void;
  onMinimize: () => void;
}

export function PopupChat({ conversation, isMinimized, onClose, onMinimize }: PopupChatProps) {
  const {
    // State
    messageInput,
    activeConversation,
    currentUser,
    messages,
    isLoading,
    selectedFiles,
    selectedFilesCount,
    isSending,
    editingMessageId,
    editingPendingMessageId,
    editMessageContent,
    animatedMessageIds,
    typingUsers,
    rateLimitState,

    // Refs
    editTextareaRef,
    scrollAreaRef,
    inputRef,
    fileInputRef,

    // Functions
    openConversation,
    sendMessage,
    setEditMessageContent,

    startEditingMessage,
    startEditingPendingMessage,
    cancelEditing,
    saveEditedMessage,
    deleteMessage,
    removePendingMessage,
    handleKeyPress,
    handleEditKeyPress,
    handleInputChange,
    handleFileChange,
    removeFile,
    handleAttachmentClick,
    isCurrentUser,
    formatFileSize,
    activeChannel,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    scrollToBottom,
    checkIfNearBottom,
    insertEmoji,
    canSendMessage,
    getWorkspaceBlockingStatus,
    isDragging,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handlePaste,
  } = useChat(conversation.id);
  const redirect = useNavigate();
  const { user } = useUserStore();

  const [showParticipants, setShowParticipants] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);
  const isMobile = useIsMobile();
  const { t } = useTranslation(["common", "chat"]);
  const MAX_FILE_COUNT = 5;

  const debouncedOpenConversation = useDebouncedCallback(openConversation, 500);

  useEffect(() => {
    debouncedOpenConversation(conversation.id);
  }, [conversation]);

  useEffect(() => {
    const scrollContainer = scrollAreaRef?.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    );

    if (scrollContainer) {
      // Use requestAnimationFrame to ensure the DOM has updated before scrolling
      requestAnimationFrame(() => {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;

        // Double-check with a small delay to ensure all content is rendered
        setTimeout(() => {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }, 100);
      });
    }
  }, [isMinimized]);

  const handleOpenReportDialog = () => {
    setReportDialogOpen(true);
  };

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
  };

  const handleSelectGroup = (group: any) => {
    // Save current scroll position before switching
    const scrollContainer = scrollAreaRef?.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    );

    if (scrollContainer) {
      setSavedScrollPosition(scrollContainer.scrollTop);
    }
    setSelectedGroup(group);
  };

  const handleBackToConversation = () => {
    setSelectedGroup(null);
    // Restore scroll position after switching back
    setTimeout(() => {
      const scrollContainer = scrollAreaRef?.current?.querySelector(
        "[data-radix-scroll-area-viewport]",
      );

      if (scrollContainer) {
        scrollContainer.scrollTop = savedScrollPosition;
      }
    }, 100);
  };

  if (!currentUser || !messages || isLoading) return null;

  return (
    <>
      {/* Minimized chat bar */}
      {isMinimized && (
        <motion.div
          layout
          animate={{ opacity: 1, y: 0 }}
          className="bg-foreground border-border/50 flex h-12 w-[260px] items-center justify-between rounded-t-lg border shadow-md"
          initial={{ opacity: 0, y: 20 }}
        >
          <div className="flex items-center space-x-2 px-3">
            <Avatar className="ring-primary-foreground/20 h-7 w-7 ring-1">
              <img
                alt={conversation.name}
                src={conversation.avatar || "/placeholder.svg?height=28&width=28"}
              />
            </Avatar>
            <span className="max-w-[140px] truncate text-sm font-medium">{conversation.name}</span>
          </div>
          <div className="flex items-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="text-muted-foreground hover:bg-muted/50 h-8 w-8 rounded-full"
                    size="icon"
                    variant="ghost"
                    onClick={onMinimize}
                  >
                    <Minimize2 className="h-4 w-4 rotate-180" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{t("common:expand")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="text-muted-foreground hover:bg-muted/50 h-8 w-8 rounded-full"
                    size="icon"
                    variant="ghost"
                    onClick={onClose}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{t("common:close")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </motion.div>
      )}

      {/* Expanded chat popup */}
      {!isMinimized && (
        <motion.div
          layout
          animate={{ opacity: 1, y: 0 }}
          className={cn(
            "bg-foreground border-border/50 relative flex flex-col overflow-hidden rounded-t-lg border shadow-xl",
            isMobile ? "right-0 bottom-0 left-0 h-[80vh] w-full" : "h-[450px] w-[320px]",
          )}
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          transition={{
            duration: 0.35,
          }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <DragDropOverlay isDragging={isDragging} />
          {/* Chat header */}
          <div className="bg-side-accent text-primary-foreground flex items-center justify-between border-b p-3">
            <div className="flex items-center gap-2">
              <Avatar className="ring-primary-foreground/20 h-8 w-8 ring-2">
                <img
                  alt={conversation.name}
                  src={conversation.avatar || "/placeholder.svg?height=32&width=32"}
                />
              </Avatar>
              <div>
                <h3 className="max-w-[160px] truncate text-sm font-medium">{conversation.name}</h3>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <DropdownMenu>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <Button
                          className="text-primary-foreground hover:bg-muted/50 h-7 w-7 rounded-full"
                          size="icon"
                          variant="ghost"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">{t("common:more")}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <DropdownMenuContent
                  align="end"
                  className="bg-side-accent border-border/50 z-[51] w-56 rounded-lg p-1 shadow-lg"
                >
                  <DropdownMenuItem
                    className="hover:bg-muted/30 flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 transition-colors"
                    onClick={() => setShowParticipants(!showParticipants)}
                  >
                    <Users className="text-primary-foreground/70 h-4 w-4" />
                    <span>{t("chat:viewParticipants")}</span>
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    className="hover:bg-muted/30 flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 transition-colors"
                    onClick={() =>
                      redirect(
                        `${user?.role === "tutor" ? TUTOR_WORKSPACE_PATHS(activeConversation?.workspace_id).CHAT : STUDENT_WORKSPACE_PATHS(activeConversation?.workspace_id).CHAT}/${activeConversation?.id}`,
                      )
                    }
                  >
                    <MessageSquare className="text-primary-foreground/70 h-4 w-4" />
                    <span>{t("chat:goToChatPage")}</span>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator className="bg-border/30 my-1" />

                  <DropdownMenuItem
                    className="hover:bg-destructive/20 text-destructive flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 transition-colors"
                    onClick={handleOpenReportDialog}
                  >
                    <Flag className="h-4 w-4" />
                    <span>{t("chat:reportConversation")}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="text-primary-foreground hover:bg-muted/50 h-7 w-7 rounded-full"
                      size="icon"
                      variant="ghost"
                      onClick={onMinimize}
                    >
                      <Minimize2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{t("common:minimize")}</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="text-primary-foreground hover:bg-muted/50 h-7 w-7 rounded-full"
                      size="icon"
                      variant="ghost"
                      onClick={onClose}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{t("common:close")}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* Back button - when in specific group view */}
          {selectedGroup && (
            <div className="bg-transparent px-2 pt-1" style={{ position: "relative", zIndex: 1 }}>
              <Button
                className="text-muted-foreground flex h-8 items-center gap-2 p-2 text-sm"
                variant="ghost"
                onClick={handleBackToConversation}
              >
                <ArrowLeft className="h-4 w-4" />
                {t("common:back")}
              </Button>
            </div>
          )}

          {/* Participants panel */}
          <AnimatePresence>
            {activeConversation && showParticipants && (
              <motion.div
                animate={{ opacity: 1, height: "auto" }}
                className={cn(
                  "bg-background absolute z-50 w-full overflow-hidden border-b",
                  selectedGroup ? "top-[100px]" : "top-[56px]",
                )}
                exit={{ opacity: 0, height: 0 }}
                initial={{ opacity: 0, height: 0 }}
              >
                <div className="p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div className={"flex items-center gap-2"}>
                      <h4 className="text-sm font-medium">{t("chat:participants")}</h4>
                      <div className="bg-muted/30 rounded-full px-2 py-0.5 text-xs font-normal">
                        {activeConversation.members.length || 0}
                      </div>
                    </div>
                    <div>
                      <Button
                        className="text-primary-foreground hover:bg-muted/50 h-8 w-8 rounded-full"
                        size="icon"
                        variant="ghost"
                        onClick={() => setShowParticipants(false)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {activeConversation.members.map((member) => (
                      <div key={member.user.id} className="flex flex-col items-center">
                        <Avatar className="ring-primary/20 mb-1 h-10 w-10 ring-1">
                          <img
                            alt={`${member.user.first_name} ${member.user.last_name}`}
                            src={member.user.avatar_path || "/placeholder.svg?height=40&width=40"}
                          />
                        </Avatar>
                        <span className="max-w-[60px] truncate text-xs">
                          {member.user.first_name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <>
            {/* Use the extracted ConversationView component */}
            <ConversationView
              activeConversation={activeConversation}
              animatedMessageIds={animatedMessageIds}
              cancelEditing={cancelEditing}
              checkIfNearBottom={checkIfNearBottom}
              currentUser={currentUser}
              deleteMessage={deleteMessage}
              disableMessageActions={canSendMessage().isWorkspaceBlocked}
              editMessageContent={editMessageContent}
              editTextareaRef={editTextareaRef}
              editingMessageId={editingMessageId}
              editingPendingMessageId={editingPendingMessageId}
              fetchNextPage={fetchNextPage}
              formatFileSize={formatFileSize}
              handleEditKeyPress={handleEditKeyPress}
              hasNextPage={hasNextPage}
              isCurrentUser={isCurrentUser}
              isFetchingNextPage={isFetchingNextPage}
              isLoading={isLoading}
              isSpecific={!!selectedGroup}
              messages={selectedGroup ? { data: [{ groups: [selectedGroup] }] } : messages}
              mode="compact"
              removePendingMessage={removePendingMessage}
              renderSpecialGroupsAsNormal={selectedGroup ? true : false}
              saveEditedMessage={saveEditedMessage}
              scrollAreaRef={scrollAreaRef}
              scrollToBottom={scrollToBottom}
              setEditMessageContent={setEditMessageContent}
              startEditingMessage={startEditingMessage}
              startEditingPendingMessage={startEditingPendingMessage}
              onGroupSelect={!selectedGroup ? handleSelectGroup : undefined}
            />

            {/* Rate limit indicator */}
            {(() => {
              const sendStatus = canSendMessage();

              // Only show rate limit indicator if not workspace blocked
              if (!sendStatus.isWorkspaceBlocked) {
                return (
                  <RateLimitIndicator
                    canSend={sendStatus.allowed}
                    cooldownEnd={rateLimitState.cooldownEnd}
                    level={rateLimitState.level}
                    remainingCooldown={rateLimitState.remainingCooldown}
                    timeUntilNextMessage={sendStatus.timeRemaining}
                  />
                );
              }

              return null;
            })()}

            {/* Workspace blocking indicator */}
            {(() => {
              const blockingStatus = getWorkspaceBlockingStatus();

              return (
                <WorkspaceBlockingIndicator
                  conversationType={activeConversation?.type || "individual"}
                  hasLeft={blockingStatus.hasLeft}
                  isBlocked={blockingStatus.isBlocked}
                />
              );
            })()}

            {typingUsers && <TypingIndicator users={typingUsers} />}

            {/* Chat input */}
            <div className="p-2">
              {/* Selected files preview */}
              <AnimatePresence>
                {selectedFilesCount > 0 && (
                  <motion.div
                    animate={{ opacity: 1 }}
                    className="mb-2 overflow-hidden"
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div className="flex flex-wrap gap-2">
                      {selectedFiles.map((fileData, index) => (
                        <FilePreview
                          key={index}
                          file={fileData.file}
                          index={index}
                          isValid={fileData.isValid}
                          validationError={fileData.validationError}
                          onRemove={removeFile}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Only render input area if user can send messages */}
              {!canSendMessage().isWorkspaceBlocked && (
                <div className="bg-side-accent flex gap-2 rounded-lg p-2 shadow-inner transition-all focus-within:ring-2 focus-within:ring-gray-600">
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    multiple
                    className="hidden"
                    type="file"
                    onChange={handleFileChange}
                  />

                  {/* Attachment button */}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          className="hover:bg-side-accent/20 text-muted-foreground hover:text-accent-foreground h-7 w-7 rounded-full p-0"
                          disabled={
                            !activeChannel ||
                            isLoading ||
                            selectedFilesCount >= MAX_FILE_COUNT
                          }
                          type="button"
                          variant="ghost"
                          onClick={handleAttachmentClick}
                        >
                          <Paperclip className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">{t("chat:attachments.attachFile")}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <Input
                    ref={inputRef}
                    className="h-7 flex-1 border-none bg-transparent text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
                    disabled={!activeChannel || isLoading}
                    placeholder={
                      activeChannel()
                        ? isSending
                          ? t("chat:input.sendingPlaceholder")
                          : t("chat:input.typingPlaceholder")
                        : t("chat:input.connectingPlaceholder")
                    }
                    value={messageInput}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyPress}
                    onPaste={handlePaste}
                  />

                  {/* Add the EmojiPickerButton here, before the send button */}
                  <EmojiPickerButton
                    onEmojiSelect={insertEmoji}
                  />

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          className="bg-primary hover:bg-primary/90 text-primary-foreground h-7 w-7 rounded-full p-0"
                          disabled={
                            !activeChannel ||
                            (!messageInput.trim() && selectedFilesCount === 0) ||
                            isLoading
                          }
                          type="submit"
                          onClick={sendMessage}
                        >
                          {isSending || isLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">{t("common:send")}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}

              {/* File upload restrictions info and storage usage */}
              <div className="text-muted-foreground relative mt-1.5 flex flex-col items-center justify-center gap-1 text-center text-[11px]">
                {activeConversation?.storage_usage && (
                  <StorageUsageIndicator storageUsage={activeConversation.storage_usage} />
                )}
              </div>
            </div>
          </>

          {/* Report Dialog */}
          <ReportDialog
            conversationId={conversation?.id}
            conversationName={conversation?.name}
            isOpen={reportDialogOpen}
            onClose={handleCloseReportDialog}
          />
        </motion.div>
      )}
    </>
  );
}
